/**
 * loginHS 빠른 세번분류 창 — /?hs-quick=1 (PC 트레이 앱의 작은 창, Ctrl+Alt+H · 트레이 아이콘 클릭). 2026-10-06.
 * 앱 셸 없이 이 화면만 그린다. 품명·규격을 넣고 Enter → 학습 모델(PROVE)의 10자리 제안과 품명을 바로 보인다(확정 아님).
 * '자세히 보기'는 loginHS 검색 결과(지도·출처별 세번·결정례)를 연다 — 트레이 앱에서는 메인 창에서 열린다.
 * 트레이가 복사해 둔 글을 주소 조각 #q= 로 미리 채운다(조각은 서버로 가지 않는다). 채우기만 하고 보내지는 않는다.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { readToken } from "./authState";
import { fetchPrecedentHints } from "./subdivisionPreviewClient";

const BLUE = "#1a56db";
const MUTED = "#6b7482";
const LINE = "#e3e6ea";
const RECENT_KEY = "loginhs:hs-quick-recent";

/** 로그인 여부 — 다른 창(트레이 앱의 메인 창)에서 로그인하면 storage 이벤트, 이 창으로 돌아오면 focus 로 다시 본다(2026-10-06). */
export function useLoggedInWatch(check) {
  const [ok, setOk] = useState(() => Boolean(check()));
  useEffect(() => {
    const again = () => setOk(Boolean(check()));
    window.addEventListener("storage", again);
    window.addEventListener("focus", again);
    document.addEventListener("visibilitychange", again);
    return () => {
      window.removeEventListener("storage", again);
      window.removeEventListener("focus", again);
      document.removeEventListener("visibilitychange", again);
    };
  }, [check]);
  return ok;
}


export function formatHsk(code) {
  const d = String(code || "").replace(/\D/g, "");
  if (d.length === 10) return `${d.slice(0, 4)}.${d.slice(4, 6)}-${d.slice(6)}`;
  if (d.length === 6) return `${d.slice(0, 4)}.${d.slice(4)}`;
  return d;
}

/** 주소 조각 #q=… — 트레이 앱은 공백을 + 로 보낸다. */
export function readFragmentQuery(hash = window.location.hash) {
  const m = String(hash || "").match(/[#&]q=([^&]*)/);
  if (!m) return "";
  try { return decodeURIComponent(m[1].replace(/\+/g, " ")).trim().slice(0, 300); } catch { return ""; }
}

function readRecent() {
  try { const v = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); return Array.isArray(v) ? v.slice(0, 6) : []; } catch { return []; }
}

function saveRecent(list) {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 6))); } catch { /* 사생활 보호 모드 */ }
}

export function searchUrl(q) {
  return `/?audit=onto-detail&sq=${encodeURIComponent(q)}`;
}

export default function QuickClassify({ fetchImpl = fetch }) {
  const [q, setQ] = useState(() => readFragmentQuery());
  const [state, setState] = useState({ status: "idle" });   // idle | loading | ok | none | error
  const [recent, setRecent] = useState(readRecent);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);
  const loggedIn = useLoggedInWatch(readToken);

  useEffect(() => {
    document.title = "loginHS 빠른 세번분류";
    inputRef.current?.focus();
    inputRef.current?.select();
    // 트레이가 같은 창에 새 글을 채워 다시 열면 조각만 바뀐다
    const onHash = () => { const v = readFragmentQuery(); if (v) { setQ(v); setState({ status: "idle" }); inputRef.current?.select(); } };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const run = useCallback(async (text) => {
    const query = String(text || "").replace(/\s+/g, " ").trim();
    if (!query) return;
    const token = readToken();
    if (!token) return;
    setState({ status: "loading", query });
    setCopied(false);
    const hints = await fetchPrecedentHints({ query, token, fetchImpl, withModel: true });
    const hsk = String(hints?.model_proposal?.hsk || "").replace(/\D/g, "");
    if (hsk.length !== 10) {
      setState({ status: hints ? "none" : "error", query });
      return;
    }
    let name = "";
    try {
      const r = await fetchImpl(`/api/onto/hsk-detail?hsk=${hsk}&lang=kor`);
      if (r && r.ok) { const j = await r.json(); name = String(j?.name_ko || j?.item?.name_ko || ""); }
    } catch { /* 품명 없이도 보인다 */ }
    setState({ status: "ok", query, hsk, name });
    setRecent((prev) => {
      const next = [{ q: query, hsk }, ...prev.filter((r) => r.q !== query)].slice(0, 6);
      saveRecent(next);
      return next;
    });
  }, [fetchImpl]);

  const copy = async (code) => {
    try { await navigator.clipboard.writeText(code); setCopied(true); } catch { /* 권한 없음 */ }
  };

  if (!loggedIn) {
    return (
      <div style={box}>
        <div style={{ ...card, textAlign: "center", color: MUTED, lineHeight: 1.8 }}>
          먼저 로그인해 주세요.<br />
          <a href="/" target="_blank" rel="noreferrer" style={{ color: BLUE }}>loginHS 열기</a>
        </div>
      </div>
    );
  }

  return (
    <div style={box}>
      <form onSubmit={(e) => { e.preventDefault(); run(q); }} style={{ display: "flex", gap: 6 }}>
        <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} aria-label="품명·규격"
          placeholder="품명·규격 (예: 실크 100% 스카프)" data-testid="hs-quick-input"
          style={{ flex: 1, minWidth: 0, padding: "9px 10px", fontSize: 14, border: `1px solid ${LINE}`, borderRadius: 8, outline: "none" }} />
        <button type="submit" disabled={!q.trim() || state.status === "loading"} data-testid="hs-quick-run"
          style={{ padding: "0 14px", border: 0, borderRadius: 8, background: BLUE, color: "#fff", fontSize: 13, cursor: "pointer", opacity: q.trim() ? 1 : 0.5 }}>
          분류
        </button>
      </form>

      {state.status === "loading" && <div style={{ ...card, color: MUTED }} role="status">분류하는 중…</div>}

      {state.status === "ok" && (
        <div style={card} data-testid="hs-quick-result">
          <div style={{ fontSize: 11, color: MUTED }}>PROVE 제안 · 확정 아님(신고 전 확인)</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 4 }}>
            <span style={{ fontFamily: "ui-monospace, Consolas, monospace", fontSize: 22, fontWeight: 700, color: BLUE }}>{formatHsk(state.hsk)}</span>
            <button type="button" onClick={() => copy(state.hsk)} style={plainBtn}>{copied ? "복사됨" : "복사"}</button>
          </div>
          {state.name && <div style={{ fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>{state.name}</div>}
          <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
            <a href={searchUrl(state.query)} target="_blank" rel="noreferrer" style={{ ...linkBtn, background: BLUE, color: "#fff", borderColor: BLUE }} data-testid="hs-quick-detail">
              자세히 보기
            </a>
            <a href={`/?audit=onto-detail&hs=${state.hsk.slice(0, 4)}&hsk=${state.hsk}`} target="_blank" rel="noreferrer" style={linkBtn}>세번 상세</a>
            <a href={`/?memo-quick=1&agent=1&hsk=${state.hsk}&text=${encodeURIComponent(state.query)}`} style={linkBtn}>메모 남기기</a>
          </div>
          <div style={{ fontSize: 11, color: MUTED, marginTop: 8, lineHeight: 1.5 }}>
            자세히 보기에서 관세율표·결정례·관세사 이력과 견주고, 가르는 사실을 확인하세요.
          </div>
        </div>
      )}

      {state.status === "none" && (
        <div style={card}>
          <div style={{ fontSize: 13 }}>지금 바로 낼 수 있는 제안이 없습니다(모델 서버가 꺼져 있거나 답하지 못했습니다).</div>
          <a href={searchUrl(state.query)} target="_blank" rel="noreferrer" style={{ ...linkBtn, display: "inline-block", marginTop: 8 }}>loginHS 에서 검색</a>
        </div>
      )}
      {state.status === "error" && (
        <div style={card}>
          <div style={{ fontSize: 13 }}>요청이 실패했습니다. 잠시 뒤 다시 하거나 loginHS 에서 검색하세요.</div>
          <a href={searchUrl(state.query)} target="_blank" rel="noreferrer" style={{ ...linkBtn, display: "inline-block", marginTop: 8 }}>loginHS 에서 검색</a>
        </div>
      )}

      {recent.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, color: MUTED, marginBottom: 4 }}>최근</div>
          {recent.map((r) => (
            <button key={r.q} type="button" onClick={() => { setQ(r.q); run(r.q); }}
              style={{ display: "flex", gap: 8, width: "100%", textAlign: "left", padding: "6px 2px", border: 0, borderBottom: `1px solid ${LINE}`, background: "transparent", cursor: "pointer", fontSize: 12 }}>
              <span style={{ fontFamily: "ui-monospace, Consolas, monospace", color: BLUE, flexShrink: 0 }}>{formatHsk(r.hsk)}</span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#1f2937" }}>{r.q}</span>
            </button>
          ))}
        </div>
      )}
      <div style={{ fontSize: 11, color: MUTED, marginTop: 12 }}>
        Enter 분류 · 복사해 둔 글이 있으면 열 때 채워집니다 · <a href="/?memo-quick=1&agent=1" style={{ color: MUTED }}>빠른 메모</a>
      </div>
    </div>
  );
}

const box = { padding: 14, fontFamily: "Pretendard, 'Malgun Gothic', system-ui, sans-serif", color: "#1f2937", background: "#fff", minHeight: "100vh", boxSizing: "border-box" };
const card = { marginTop: 12, padding: 12, border: `1px solid ${LINE}`, borderRadius: 8 };
const plainBtn = { border: `1px solid ${LINE}`, background: "#fff", borderRadius: 6, padding: "2px 8px", fontSize: 12, cursor: "pointer", color: "#1f2937" };
const linkBtn = { border: `1px solid ${LINE}`, borderRadius: 6, padding: "5px 10px", fontSize: 12, textDecoration: "none", color: "#1f2937" };
